import { IFileInfoPlugin } from '@src/slide/application/no-edit/plugins/file-info/file-info-plugin.interface';
import { NoEditPlugin, PluginProps } from '@src/slide/application/no-edit/plugins/no-edit-plugin';
import {
    FileInfoRightBarModule,
    MODULE_NAME,
} from '@src/slide/application/no-edit/plugins/file-info/file-info-right-bar-module';
import { IExtension } from '@tencent/ark-extensions';
import NoEditArkLoader from '@src/slide/application/no-edit/common/no-edit-ark-extensions';
import { FILE_INFO_EXTENSION_CONFIG } from '@src/slide/application/no-edit/common/no-edit-ark-extensions/dc-components/file-info/component-config';
import { browser, EDIT_TYPE } from '@src/slide/application/service/edit-extension/edit-extension-export';
import './index.less';
import { IReadRecordPlugin } from '@src/slide/application/no-edit/plugins/read-record/read-record-plugin.interface';
import { READ_RECORD_PLUGIN_CONFIG } from '@src/slide/application/no-edit/plugins/read-record/plugin-config';
import { AUTH_PC_PLUGIN_CONFIG_NAME } from '@src/slide/application/no-edit/plugins/auth/constants';
import { DOC_SIZE_EXTENSION_CONFIG } from '../../common/no-edit-ark-extensions/dc-components/doc-size/component-config';
import { SlideDataServices } from '@src/slide/domain/slide-app/core/slide-data-services.core';
import { DOC_SIZE_MODULE_NAME, DocSizeRightBarModule } from './doc-size-right-side-bar-module';
import { IPcEditorExtensionService } from '@src/slide/application/service/edit-extension/core/editor/pc/interface';
import { IAuthPluginBase } from '@src/slide/application/no-edit/plugins/auth/base/base/auth-plugin-base.interface';
import { Events } from '@tencent/docs-titlebar-service';
import { InjectorService, EventEmitter } from '@tencent/workbench';

const HISTORY_PANEL = 'history';
const VERSION_PANEL = 'version';

export default class FileInfoPlugin extends NoEditPlugin implements IFileInfoPlugin {
    declare protected editExtensionService?: IPcEditorExtensionService;
    private extension?: IExtension;
    private eventEmitter: EventEmitter;
    // 修改后的构造函数
    public constructor(pluginProps: PluginProps) {
        super(pluginProps); // 必须传递父类参数
        this.eventEmitter = InjectorService.get(EventEmitter);
    }

    public initPlugin(): void {
        this.editExtensionService?.registerRightSideBarModule(new FileInfoRightBarModule(this));
        this.editExtensionService?.registerRightSideBarModule(new DocSizeRightBarModule(this));
        this.editExtensionService?.watchEditTypeChange?.((editType: string) => {
            if (editType === EDIT_TYPE.MASTER_LAYOUT) {
                // 隐藏文件信息侧边栏
                this.hide();
            }
        });
    }

    public showModule(DOMNode: Element | Text | null, onShow: () => void, onClose: () => void): void {
        const moduleOptions = browser.isPad ? {
            // 「关闭按钮」被点击
            onClose,
            // 「位置」被点击
            onClickLocation: () => {},
            // 「权限」被点击
            onClickPermission: this.handleAuthShow,
            // 「最近查看」被点击
            onClickRecently: () => {},
            // 「历史版本」被点击
            onClickHistory: () => {},
            // 「最近修改」被点击
            onClickLastModify: () => {},
            // 「文档大小」被点击
            onClickDocSize: () => this.showDocSizePanel(),
        } : {
            // 「关闭按钮」被点击
            onClose,
            // 「权限」被点击
            onClickPermission: this.handleAuthShow,
            // 「最近查看」被点击
            onClickRecently: this.handleReadRecordShow,
            // 「历史版本」被点击
            onClickHistory: () => this.handleRevisionRecordShow(VERSION_PANEL),
            // 「最近修改」被点击
            onClickLastModify: () => this.handleRevisionRecordShow(HISTORY_PANEL),
            // 「文档大小」被点击
            onClickDocSize: () => this.showDocSizePanel(),
        };
        this.loadModule(onShow).then(() => {
            this.extension?.show({
                container: DOMNode,
                ...moduleOptions,
            });
        });
    }

    public showDocSizeModule(domNode: Element | Text | null, onShow: () => void, onHide: () => void): void {
        if (!domNode) return;
        this.loadDocSizeModule(domNode, onShow, onHide);
    }

    public async showDocSizePanel(): Promise<void> {
        const docInfo = await SlideDataServices.DocInfoManager.promise();
        // 文档所有者有权限打开文档大小面板
        if (docInfo.isOwner) {
            // 绕一大圈最终触发 showDocSizeModule 💦
            this.editExtensionService?.showRightSideBar(DOC_SIZE_MODULE_NAME);
        }
    }

    public hideDocSizePanel(): void {
        this.editExtensionService?.hideRightSideBar();
    }

    public removeModule(): void {
        this.extension?.hide();
        this.extension = undefined; // 移除时不再抓引用
    }

    public show(): void {
        if (!this.isShown()) {
            this.editExtensionService?.showRightSideBar(MODULE_NAME);
        }
    }

    public hide(): void {
        this.editExtensionService?.hideRightSideBar();
    }

    // 打开权限面板
    private handleAuthShow = async () => {
        // 使用Events.SHOW_COOPERATION 触发的方式 调用权限设置
        // 需要先关闭侧边栏，否则会出现覆盖问题（第二次触发的侧边栏在第一次下面）
        this.editExtensionService?.hideRightSideBar();
        this.eventEmitter.emit(Events.SHOW_COOPERATION);
    };

    // 打开修订记录面板
    private handleRevisionRecordShow = (panelId: string) => {
        this.editExtensionService?.showHistoryRightBar({
            activeId: panelId,
        });
    };

    // 打开浏览记录面板
    private handleReadRecordShow = async () => {
        const plugin = await this.noEditService?.getPlugin<IReadRecordPlugin>(READ_RECORD_PLUGIN_CONFIG.name);
        plugin?.show();
    };

    private async loadDocSizeModule(dom: Element | Text, onShow: () => void, onHide: () => void) {
        const docSizePanel = await NoEditArkLoader.extensionService.getExtension(DOC_SIZE_EXTENSION_CONFIG.name);
        const docInfo = await SlideDataServices.DocInfoManager.promise();
        docSizePanel.show({
            container: dom,
            onClose: () => {
                docSizePanel.hide();
                onHide();
            },
            globalPadId: docInfo.globalPadId,
            mobileMode: browser.isMobile && !browser.isPad,
        });
        onShow();
    }

    /**
     * 加载DC组件
     * @param onShow
     */
    private async loadModule(onShow: () => void) {
        return new Promise((resolve, reject) => {
            NoEditArkLoader.extensionService
                .getExtension(FILE_INFO_EXTENSION_CONFIG.name)
                .then((fileInfoPanel) => {
                    resolve(fileInfoPanel);
                    this.extension = fileInfoPanel;
                    onShow?.();
                })
                .catch((err) => {
                    window.log.warn('文件信息面板加载失败');
                    reject(err);
                });
        });
    }

    private isShown(): boolean {
        return !!this.editExtensionService?.isShowRightSideBar(MODULE_NAME)
            || !!this.editExtensionService?.isShowRightSideBar(DOC_SIZE_MODULE_NAME);
    }
}
